import { useState } from 'react';
import type { InsuranceArticleCategory } from '../data/insuranceArticleTypes';

type InsuranceArticleSidebarProps = {
  sidebarTitle: string;
  categories: InsuranceArticleCategory[];
  activeHref?: string;
  onNavigate: (href: string) => void;
  defaultOpenCategory?: string;
};

function InsuranceArticleSidebar({
  sidebarTitle,
  categories,
  activeHref,
  onNavigate,
  defaultOpenCategory,
}: InsuranceArticleSidebarProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>(() =>
    categories.reduce<Record<string, boolean>>((acc, category) => {
      acc[category.name] = category.name === (defaultOpenCategory ?? categories[0]?.name);
      return acc;
    }, {})
  );

  const toggleCategory = (categoryName: string) => {
    setOpenCategories((prev) => ({ ...prev, [categoryName]: !prev[categoryName] }));
  };

  return (
    <aside className="card" style={{ alignSelf: 'flex-start', paddingBottom: '0.75rem' }}>
      <nav>
        <button
          type="button"
          style={{
            width: '100%',
            textAlign: 'left',
            background: 'none',
            border: 'none',
            padding: '0.5rem 0.25rem 0.5rem 0',
            fontFamily: 'inherit',
            fontSize: '0.95rem',
            fontWeight: 600,
            cursor: 'pointer',
            color: '#111827',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
          aria-expanded={isOpen}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <span>{sidebarTitle}</span>
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            aria-hidden="true"
            focusable="false"
            style={{
              display: 'block',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <path
              d="M3 4L6 8L9 4"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {isOpen &&
          categories.map((category) => (
            <div key={category.name} style={{ marginBottom: '0.35rem' }}>
              <button
                type="button"
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  color: '#6B7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  padding: '0.35rem 0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                }}
                aria-expanded={openCategories[category.name]}
                onClick={() => toggleCategory(category.name)}
              >
                <span>{category.name}</span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  aria-hidden="true"
                  focusable="false"
                  style={{
                    display: 'block',
                    transform: openCategories[category.name] ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                >
                  <path
                    d="M3 4L6 8L9 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {openCategories[category.name] && (
                <ul style={{ listStyle: 'none', paddingLeft: '0.5rem', margin: 0 }}>
                  {category.articles.map((article) => {
                    const isActive = !!activeHref && article.href === activeHref;
                    const isClickable = article.status === 'published' && !!article.href && !isActive;

                    return (
                      <li key={article.title} style={{ marginBottom: '0.3rem' }}>
                        <button
                          type="button"
                          style={{
                            background: isActive ? '#ECFDF5' : 'none',
                            border: isActive ? '1px solid var(--color-green-light)' : 'none',
                            borderRadius: isActive ? '0.5rem' : 0,
                            padding: isActive ? '0.45rem 0.55rem' : '0.35rem 0.25rem',
                            fontFamily: 'inherit',
                            fontSize: '0.9rem',
                            color: isActive ? '#065F46' : article.status === 'published' ? '#111827' : '#4B5563',
                            cursor: isClickable ? 'pointer' : 'default',
                            textAlign: 'left',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '0.4rem',
                            fontWeight: isActive ? 700 : article.status === 'published' ? 600 : 500,
                          }}
                          onClick={() => {
                            if (isClickable && article.href) onNavigate(article.href);
                          }}
                          aria-current={isActive ? 'page' : undefined}
                        >
                          <span>{article.title}</span>
                          {article.status === 'coming_soon' && (
                            <span
                              style={{
                                fontSize: '0.65rem',
                                background: '#F59E0B',
                                color: '#111827',
                                borderRadius: '999px',
                                padding: '0.08rem 0.4rem',
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            >
                              Coming soon
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ))}
      </nav>
    </aside>
  );
}

export default InsuranceArticleSidebar;
